import {generatePath} from 'react-router-dom';

import {apiClient} from 'core/apiClient';
import {IS_LOCALHOST} from 'core/constants/env';
import {GdsStatus, ProductsStatus} from 'core/dictionaries';
import {isAdmin$, profile$} from 'models/auth';
import {fetchInvoicesFilters} from 'models/documents/invoices/common';
import {DocumentDetail, DocumentsAccessors, DocumentsGridRawRow, ProductContentRawRow} from 'types';
import {
    ApiFilters,
    ApiPaging,
    ApiSortings,
    SimplePaginationContent,
    SimplePaginationContentRaw,
} from 'types/api';
import {ProductsUPDItem} from 'types/documents';

const DOCUMENTS_URL = '/service/documents';
const DOCUMENTS_URL_MARKING = '/service/documents/custom';

let gridTokensCache: Record<number, string | null> = {
    0: null,
};

export function clearDocumentsGridCache(): void {
    gridTokensCache = {
        0: null,
    };
}

export function modifyParams(params: {
    sortings: ApiSortings;
    filters: ApiFilters;
    paging: ApiPaging;
    search: string;
    updOnly: boolean;
}) {
    const createdDateRange = params.filters.find(
        (filter) => filter.id === DocumentsAccessors.createdDate,
    );
    if (createdDateRange && Array.isArray(createdDateRange?.value)) {
        const createdDateRangeFlattered = createdDateRange.value[0];
        if (createdDateRange?.value?.length) {
            // @ts-ignore
            createdDateRange.value = createdDateRangeFlattered;
        }
    }

    if (params.updOnly) {
        const newFilter = {
            id: DocumentsAccessors.side,
            value:
                location.pathname === '/invoices/incoming'
                    ? ['INCOMING']
                    : location.pathname === '/invoices/outgoing'
                    ? ['OUTGOING']
                    : [],
        };
        params.filters.push(newFilter);
    } else {
        const typeFilter = params.filters.find((filter) => filter.id === DocumentsAccessors.type);
        if (typeFilter?.value?.length) {
            const [value] = typeFilter?.value;
            //@ts-ignore
            params.type = value;
        }
    }

    if (!location.pathname.startsWith('/invoices/')) {
        const createdAt = params.filters.find(
            (filter) => filter.id === DocumentsAccessors.createdDate,
        );
        if (createdAt?.value?.length) {
            const [value] = createdAt?.value;
            //@ts-ignore
            const date = new Date(value);
            date.setUTCMinutes(date.getUTCMinutes() - date.getTimezoneOffset());
            //@ts-ignore
            params.createdAt = date.toISOString();
        }
    }

    if (params.search) {
        //@ts-ignore
        params.docOrOwner = params.search;
    }
    //@ts-ignore
    delete params.search;
}
interface FetchParamsType {
    sortings: ApiSortings;
    filters: ApiFilters;
    paging: ApiPaging;
    search: string;
    updOnly: boolean;
    pageRef?: string | null;
}

export async function fetchDocumentsGrid(
    params: FetchParamsType,
): Promise<SimplePaginationContent<DocumentsGridRawRow>> {
    const EMPTY_DATA = {
        content: [],
        hasNext: false,
        nextPageRef: '',
    };
    if (location.pathname === '/invoices') {
        return EMPTY_DATA;
    }
    const {pageRef, ...restParams} = params;
    modifyParams(restParams);
    const {pageSize, pageNumber} = restParams.paging;
    const mappedPaging = pageNumber === 0 ? {pageSize} : {pageRef};
    const responseData: SimplePaginationContentRaw<DocumentsGridRawRow> = await apiClient
        .post(DOCUMENTS_URL_MARKING, {
            ...restParams,
            paging: mappedPaging,
        })
        .then((res) => res.data)
        .catch(() => EMPTY_DATA);

    // const profile = profile$.getState();
    // const isAdmin = isAdmin$.getState();
    //
    // if (!isAdmin)
    //     responseData?.data?.map(
    //         (row) => profile?.client?.fullName && (row.owner = profile.client.fullName),
    //     );

    if (
        restParams.updOnly &&
        restParams.filters.length === 1 &&
        restParams.filters[0].id === 'side'
    ) {
        fetchInvoicesList(restParams).then((data) => {
            fetchInvoicesFilters(data.data);
        });
    }

    // if (responseData?.data?.length && !location.pathname.startsWith('/invoices/'))
    //     await Promise.all(
    //         responseData.data.map((row) =>
    //             fetchDocumentsDetail(row.reportId)
    //                 .then((detail) => {
    //                     //@ts-ignore
    //                     row.buyer = detail?.reportInfo?.buyerLegalName ?? '';
    //                     //@ts-ignore
    //                     row.seller = detail?.reportInfo?.sellerLegalName ?? '';
    //
    //                     return row;
    //                 })
    //                 .catch(console.error),
    //         ),
    //     );

    return {
        content: responseData.data,
        hasNext: !!responseData.nextPageRef,
        nextPageRef: responseData.nextPageRef,
    };
}

export async function fetchDocumentsDetail(id: string): Promise<DocumentDetail> {
    return (
        await apiClient.post(`${DOCUMENTS_URL}/details`, {
            reportId: id,
        })
    ).data;
    // const result: any = await reportDetail();
    // return result;
}

export async function fetchInvoiceDetail(id: string | null): Promise<string> {
    if (!id) {
        return '';
    }
    const {data} = await apiClient.get(`/marking/invoice/body`, {
        params: {
            reportId: id,
        },
    });

    if (data) {
        return data;
    }

    throw new Error('File is Empty');
}

export async function fetchTinLegalName(params?: {
    combinedSearch: string;
}): Promise<
    {
        city: string;
        legalName: string;
        postalCode: string;
        taxIdNumber: string;
    }[]
> {
    const {data} = await apiClient.get(`/service/dict/tin-legal-name`, {
        params,
    });

    return data ?? '';
}

export async function fetchStorageLoaction(
    taxIdNumber: string,
): Promise<
    {
        storageName: string;
        address: string;
    }[]
> {
    const {data} = await apiClient.get(`service/dict/storage-location`, {
        params: {
            taxIdNumber,
        },
    });

    return data ?? '';
}

export async function deleteInvoice(invoiceId: string): Promise<any> {
    if (!invoiceId) {
        return;
    }

    const {data} = await apiClient.post(`/marking/invoice/delete?reportId=${invoiceId}`);

    return {
        data,
    };
}

export async function updateInvoiceStatus(params: {
    invoiceId: string;
    status: string;
}): Promise<any> {
    if (!params.invoiceId || !params.status) {
        return;
    }

    const {data} = await apiClient.post(
        `/marking/invoice/update-status?reportId=${params.invoiceId}&reportStatus=${params.status}`,
    );

    return {
        data,
    };
}

export async function fetchDocumentProductsGrid(params: {
    reportId: string;
    paging: ApiPaging;
    sortings: ApiSortings;
    filters: ApiFilters;
}): Promise<ProductContentRawRow[]> {
    return (await apiClient.post(`${DOCUMENTS_URL}/details/products`, params))
        .data as ProductContentRawRow[];
}

export async function fetchInvoicesList(params: Omit<FetchParamsType, 'pageRef'>): Promise<any> {
    const {data} = await apiClient.post(`${DOCUMENTS_URL_MARKING}/dict`, params);
    return {
        data,
    };
}

export async function fetchUPDProductsGrid(params: {
    reportId: string;
    paging: ApiPaging;
    sortings: ApiSortings;
    filters: ApiFilters;
}): Promise<ProductsUPDItem[]> {
    return (await apiClient.post(`${DOCUMENTS_URL}/details/upd-products`, params))
        .data as ProductsUPDItem[];
}

export async function downloadInvoicePDF(id?: string) {
    if (!id) throw Error('No ID');
    const path =
        (IS_LOCALHOST
            ? 'https://lk-participant-ngdev.pmark.tracecore.solutions'
            : window.location.origin) + generatePath('/invoices/print/:id', {id});

    return (
        await apiClient.post<Blob>('/pdf', {url: path}, {baseURL: '/api', responseType: 'blob'})
    ).data;
}

interface FetchGdsGridParams extends Omit<FetchParamsType, 'updOnly' | 'search' | 'pageRef'> {
    searchField: string;
}
export async function fetchGdsGrid(params: FetchGdsGridParams): Promise<any> {
    const {filters, ...restParams} = params;
    const updatedFilters = filters.map((filter) => {
        if (filter.id === 'receiptedAt') {
            return {
                id: filter.id,
                value: filter.value[0],
            };
        }
        return filter;
    });
    const updatedParams = {
        ...restParams,
        filters: updatedFilters,
        // paging: {
        //     pageSize: paging.pageSize,
        //     pageNumber: paging.pageNumber + 1,
        // },
    };
    return (await apiClient.post('/customs/declaration/page', updatedParams)).data;
}

export interface GoodsDeclarationDetails {
    id: string;
    participantName: string;
    participantTin: string;
    storageLocation: string;
    receiptedAt: string;
    status: GdsStatus;
    gdNumber: string | null;
    customsMarkingCodeList: {
        code: string;
        storageLocation?: string;
        updatedAt?: string;
        objectsNumber?: number;
        status?: string;
    }[];
    customsId: number | string;
    legalIdentityId: number;
}
export async function fetchGdsDetail(id: string): Promise<GoodsDeclarationDetails> {
    return (await apiClient.get(`/customs/declaration/${id}`)).data;
}

export async function createGds(params: {
    customsId: number;
    storageLocation: string;
    markingCodeList: string[];
}): Promise<string> {
    return (await apiClient.post('/customs/declaration', params)).data;
}

export async function saveChangesGdsInfo(
    id: string,
    params: {
        customsId: number;
        storageLocation: string;
    },
): Promise<string> {
    return (await apiClient.put(`/customs/declaration/${id}`, params)).data;
}

export async function saveChangesGdsCodeList(
    id: string,
    params: {
        [key: string]: boolean;
    },
): Promise<string> {
    return (await apiClient.put(`/customs/declaration/codes/${id}`, params)).data;
}

export async function submitGds(id: string): Promise<string> {
    return (await apiClient.put(`/customs/declaration/submit/${id}`)).data;
}

export async function getCustoms(): Promise<
    {
        address: string;
        id: number;
        name: string;
        number: string;
    }[]
> {
    const data = await apiClient.post('/customs/page', {});
    return data.data.data;
}
